import {
  DefaultChatTransport,
  type ChatTransport,
  type HttpChatTransportInitOptions,
  type UIMessage,
  type UIMessageChunk,
} from "ai";

/**
 * DefaultChatTransport reports a 204 resume as `null`, but useChat does not
 * expose that distinction to callers. Delay dropping the partial assistant
 * message until a real replay stream is available so a 204 cannot erase it.
 *
 * It also refuses a second resume while one is still connecting. React
 * StrictMode runs useChat's `resume: true` mount effect twice and the SDK has
 * no concurrency guard: two replays into one Chat push a fresh copy of the
 * reply on every chunk. useChat reads `null` as "nothing to resume".
 */
export class ReconnectAwareChatTransport<
  UI_MESSAGE extends UIMessage,
> extends DefaultChatTransport<UI_MESSAGE> {
  private pendingReconnect: Promise<ReadableStream<UIMessageChunk> | null> | null =
    null;

  constructor(
    options: HttpChatTransportInitOptions<UI_MESSAGE>,
    private readonly onResponseStream: () => void,
    private readonly onReconnectStream: () => void,
    private readonly onReconnectUnavailable: () => void = () => undefined
  ) {
    super(options);
  }

  override async sendMessages(
    options: Parameters<ChatTransport<UI_MESSAGE>["sendMessages"]>[0]
  ): Promise<ReadableStream<UIMessageChunk>> {
    const stream = await super.sendMessages(options);
    this.onResponseStream();
    return stream;
  }

  override async reconnectToStream(
    options: Parameters<ChatTransport<UI_MESSAGE>["reconnectToStream"]>[0]
  ): Promise<ReadableStream<UIMessageChunk> | null> {
    if (this.pendingReconnect) return null;
    this.pendingReconnect = super.reconnectToStream(options);
    let stream: ReadableStream<UIMessageChunk> | null;
    try {
      stream = await this.pendingReconnect;
    } finally {
      this.pendingReconnect = null;
    }
    if (stream) {
      this.onResponseStream();
      this.onReconnectStream();
    } else {
      this.onReconnectUnavailable();
    }
    return stream;
  }
}
